import * as React from "react";
import { loginUser } from "../actions/loginWorker";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { TextField } from "@fluentui/react/lib/TextField";
import { Label } from "@fluentui/react/lib/Label";
import { Icon } from "@fluentui/react/lib/Icon";
import { getParameterByName } from "../actions/loginWorker";
import {
	APICalls,
	setXplanURL,
	getXplanURL,
	appConfig,
	watchForAuthCode,
	getUserStorySessionGuid,
	validateSiteUrl,
	exampleUrl
} from "../actions/helper";
import { APP_VERSION } from "../appConfig";
import { get_capabilities } from "../actions/entityWorker";
import { throwError, toggleLoading } from "../actions/navigationWorker";
import { LogError } from "../actions/errorWorker";
import { initializeIcons } from "@fluentui/react/lib/Icons";

initializeIcons();

export interface AppState {
	username: string;
	password: string;
	xplan_url: string;
	username_error: string;
	password_error: string;
	xplan_url_error: string;
	hide_xplan_url: boolean;
}

function showNewPluginAlert(hide_xplan_url): boolean {
	if (hide_xplan_url) return false;
	let timestamp = 0;
	const nextTimestamp = localStorage.getItem("showNewPluginAlertTimestamp");
	if (nextTimestamp) timestamp = parseInt(nextTimestamp);

	const noIE = !navigator.userAgent.includes("Trident");

	const platform = Office.context.platform || Office.context.diagnostics.platform;
	const isDesktop = platform === Office.PlatformType.PC || platform === Office.PlatformType.Mac;

	return Date.now() > timestamp && noIE && isDesktop;
}

const day = 86400000;

class Login extends React.Component<any, any> {
	constructor(props, context) {
		super(props, context);
		this.state = {
			username: "",
			password: "",
			otp: "",
			xplan_url: this.props.xplan_url,
			username_error: "Username is required",
			password_error: "Password is required",
			xplan_url_error: "XPLAN Site URL is required",
			xplan_invalid_url: `Invalid Xplan URL. Please enter the url in this format ${exampleUrl()}`,
			hide_xplan_url: false,
			newPluginAlertVisible: false,
		};
	}

	hideAlert = () => {
		this.setState({ newPluginAlertVisible: false });
		localStorage.setItem("showNewPluginAlertTimestamp", String(Date.now() + day));
	};

	developerLogin = () => {
		this.setState({
			username: localStorage.getItem("devLoginUser") || "",
			password: localStorage.getItem("devLoginPassword") || ""
		});
	};

	componentDidMount() {
		const hideUrlFor = localStorage.getItem("hide_url_for") || "";
		let hiddenUrl = appConfig().hidden_urls[hideUrlFor];
		
		if (!hiddenUrl && hideUrlFor) {
			const matchingKey = Object.keys(appConfig().hidden_urls).find(
				key => key.toLowerCase() === hideUrlFor.toLowerCase()
			);
			if (matchingKey) {
				hiddenUrl = appConfig().hidden_urls[matchingKey];
				localStorage.setItem("hide_url_for", matchingKey);
			}
		}
		
		if (hiddenUrl) {
			this.setState({ hide_xplan_url: true });
			this.handleXplanURL(null, hiddenUrl.url);
		}
		
		this.developerLogin();
		
		if (this.state.xplan_url) {
			setXplanURL(this.state.xplan_url);
		}
		
		if (showNewPluginAlert(this.state.hide_xplan_url)) {
			this.setState({ newPluginAlertVisible: true });
		}
	}

	handleUsername = (_event, item) => {
		this.setState({ username: item });
	};

	handlePassword = (_event, item) => {
		this.setState({ password: item });
	};

	handleOpt = (_event, item) => {
		this.setState({ otp: item });
	};

	handleXplanURL = (_, url: string) => {
		let cleanUrl = url.trim().toLowerCase();
		if (cleanUrl.endsWith("/")) {
			cleanUrl = cleanUrl.slice(0, -1);
		}
		this.setState({ xplan_url: cleanUrl });
		setXplanURL(cleanUrl);
	};

	private handleLoginClick = () => {
		try {
			if (!this.ValidateFields()) {
				return;
			}
			localStorage.setItem("is_sso_login", "false");
			localStorage.removeItem("capabilities_results");
			localStorage.removeItem("xplan_cookie");
			localStorage.removeItem("xplan_cookie_expire");
			setXplanURL(this.state.xplan_url);

			loginUser(
				getXplanURL() + APICalls.User,
				this.state.username,
				this.state.password,
				this.state.otp,
				get_capabilities,
				null
			);
		} catch (err) {
			throwError(err);
			LogError(err, {}, "handleLoginClick", "ERROR");
		}
	};

	private handleSSOLoginClick = () => {
		try {
			if (!this.ValidateXplanURL()) {
				return;
			}

			localStorage.setItem("is_sso_login", "true");
			localStorage.removeItem("auth_code");
			localStorage.removeItem("user_story_session_guid");
			localStorage.removeItem("capabilities_results");
			localStorage.removeItem("xplan_cookie");
			localStorage.removeItem("xplan_cookie_expire");
			localStorage.removeItem("access_token");
			localStorage.removeItem("refresh_token");
			localStorage.removeItem("auth_token_type");

			setXplanURL(this.state.xplan_url);
			
			const sessionGuid = getUserStorySessionGuid();
			console.log(`[SSO Login] Generated session GUID: ${sessionGuid}`);
			
			const oauth_url = `${this.state.xplan_url}/oauth2/auth?client_id=${appConfig().app_client_id}&response_type=code`.replace("https://", "");
			const callback_redirect_url = `${appConfig().redirect_url}?uss_guid=${sessionGuid}&oath_url=${oauth_url}`;

			console.log("[SSO Login] Opening browser for authentication");
			
			const platform = Office?.context?.platform;
			const isMobile = platform === Office.PlatformType.iOS || platform === Office.PlatformType.Android;
			const isMac = platform === Office.PlatformType.Mac;
			
			// Detect if we're in New Outlook (web-based) vs Classic Outlook (desktop)
			// New Outlook typically runs in a web context and may not have full Office API support
			const isNewOutlook = Office?.context?.mailbox?.diagnostics?.hostName === "OutlookWebApp" ||
			                     Office?.context?.mailbox?.diagnostics?.hostName === "Outlook" && 
			                     Office?.context?.mailbox?.diagnostics?.hostVersion?.includes("web");
			
			let windowOpened = false;
			
			// For Mac, use window.open first as it's more reliable with popup blockers
			if (isMac) {
				try {
					console.log("[SSO Login] Mac detected - using window.open with specific features");
					const windowFeatures = "width=600,height=800,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes";
					const authWindow = window.open(callback_redirect_url, "_blank", windowFeatures);
					
					if (authWindow && !authWindow.closed) {
						console.log("[SSO Login] Window opened successfully on Mac");
						windowOpened = true;
						// Start polling only after window opens successfully
						watchForAuthCode();
					} else {
						console.warn("[SSO Login] Window was blocked or failed to open");
					}
				} catch (macError) {
					console.warn("[SSO Login] window.open failed on Mac:", macError);
				}
			}
			
			// For classic Outlook Desktop (non-Mac), use openBrowserWindow
			if (!windowOpened && !isNewOutlook && Office?.context?.ui?.openBrowserWindow) {
				try {
					console.log("[SSO Login] Using openBrowserWindow (Classic Outlook)");
					Office.context.ui.openBrowserWindow(callback_redirect_url);
					windowOpened = true;
					// Start polling only after window opens
					watchForAuthCode();
				} catch (openBrowserError) {
					console.warn("[SSO Login] openBrowserWindow failed:", openBrowserError);
				}
			}
			
			// For New Outlook or mobile, or as fallback
			if (!windowOpened) {
				try {
					const link = document.createElement('a');
					link.href = callback_redirect_url;
					link.target = '_blank';
					link.rel = 'noopener noreferrer';
					link.style.display = 'none';
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					console.log("[SSO Login] Used programmatic link click");
					windowOpened = true;
					// Start polling
					watchForAuthCode();
				} catch (e) {
					console.warn("[SSO Login] Link click failed:", e);
					
					// Fallback for mobile
					if (isMobile && Office?.context?.ui?.displayDialogAsync) {
						console.log("[SSO Login] Using displayDialogAsync (Mobile fallback)");
						Office.context.ui.displayDialogAsync(
							callback_redirect_url,
							{ height: 60, width: 50 },
							(asyncResult) => {
								if (asyncResult.status === Office.AsyncResultStatus.Failed) {
									console.error("[SSO Login] displayDialogAsync failed:", asyncResult.error);
									throwError("Failed to open SSO login dialog");
								} else {
									const dialog = asyncResult.value;
									windowOpened = true;
									// Start polling after dialog opens
									watchForAuthCode();
									dialog.addEventHandler(Office.EventType.DialogEventReceived, (eventArg) => {
										console.log("[SSO Login] Dialog event received:", eventArg);
									});
								}
							}
						);
					} else {
						// Final fallback
						console.log("[SSO Login] Using window.open (final fallback)");
						const finalWindow = window.open(callback_redirect_url, "_blank");
						if (finalWindow) {
							windowOpened = true;
							watchForAuthCode();
						}
					}
				}
			}
			
			// If no window was opened, don't start polling
			if (!windowOpened) {
				console.error("[SSO Login] Failed to open authentication window");
				throwError("Unable to open authentication window. Please allow popups for this site.");
			}
		} catch (err) {
			throwError(err);
			LogError(err, {}, "handleSSOLoginClick", "ERROR");
		}
	};

	keyPress = (e) => {
		if (e.keyCode === 13) {
			this.handleLoginClick();
		}
	};

	private ValidateFields = (): boolean => {
		if (this.state.username === "") {
			throwError(this.state.username_error);
			return false;
		}

		if (this.state.password === "") {
			throwError(this.state.password_error);
			return false;
		}

		if (this.state.xplan_url === "") {
			throwError(this.state.xplan_url_error);
			return false;
		}
		if (!validateSiteUrl(this.state.xplan_url)) {
			throwError(this.state.xplan_invalid_url);
			return false;
		}
		return true;
	};

	private ValidateXplanURL = (): boolean => {
		if (this.state.xplan_url === "") {
			throwError(this.state.xplan_url_error);
			return false;
		}

		if (!validateSiteUrl(this.state.xplan_url)) {
			throwError(this.state.xplan_invalid_url);
			return false;
		}

		return true;
	};

	private handleClearCache = () => {
		try {
			console.log("[Clear Cache] Clearing all storage and cache...");
			
			// Clear localStorage
			localStorage.clear();
			
			// Clear sessionStorage
			sessionStorage.clear();
			
			// Reload the page to reset the app
			window.location.reload();
		} catch (err) {
			console.error("[Clear Cache] Error clearing cache:", err);
			LogError(err, {}, "handleClearCache", "ERROR");
		}
	};

	render() {
		const container = document.getElementById("MainBody");
		const access_token = getParameterByName("useruniqueaccesstoken", null);
		const scriptId = getParameterByName("scriptID", null);
		const hide_all = (access_token && scriptId) || this.state.hide_xplan_url;
		if (this.props.hide) {
			if (container) {
				container.style.backgroundColor = "#ffffff";
			}
			return null;
		}

		if (container) {
			container.style.backgroundColor = "#3a1c46";
		}

		let login_error = "";
		if (this.props.logged_in === false) {
			login_error = this.props.error;
		}
		let style = {};
		if (login_error.length === 0) {
			style = { display: "none" };
		}

		let OTPStyle = { display: "none" };
		if (this.props.IsTFA) {
			OTPStyle = { display: "block" };
			toggleLoading(false);
		}

		return (
			<div className="ms-u-textAlignCenter" style={{ 
				padding: "0", 
				minHeight: "100vh", 
				display: "flex", 
				alignItems: "center",
				justifyContent: "center",
				background: "linear-gradient(135deg, #1A1F4D 0%, #5D3FD6 100%)"
			}}>
				<div style={{ 
					width: "90%", 
					maxWidth: "420px",
					backgroundColor: "#fff",
					borderRadius: "12px",
					padding: "32px",
					boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
					border: "2px solid #23F0C7",
					position: "relative"
				}}>
					<button
						title="Clear Cache"
						onClick={this.handleClearCache}
						style={{
							backgroundColor: "#5D3FD6",
							color: "#ffffff",
							height: "36px",
							width: "36px",
							minWidth: "36px",
							borderRadius: "6px",
							border: "none",
							cursor: "pointer",
							fontSize: "16px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transition: "all 0.2s",
							boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
							position: "absolute",
							top: "8px",
							right: "8px"
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.backgroundColor = "#4A2FC7";
							e.currentTarget.style.transform = "scale(1.05)";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.backgroundColor = "#5D3FD6";
							e.currentTarget.style.transform = "scale(1)";
						}}
					>
						<Icon iconName="Refresh" />
					</button>
					<h2 style={{
						marginTop: 0,
						marginBottom: "24px",
						fontSize: "24px",
						fontWeight: 600,
						color: "#1A1F4D",
						textAlign: "center"
					}}>
						Welcome
					</h2>
					{!this.state.hide_xplan_url && (
						<TextField
							name="xplan_url"
							defaultValue={this.state.xplan_url}
							placeholder="XPLAN Site URL"
							onChange={this.handleXplanURL}
							styles={{ 
								root: { marginBottom: "16px" },
								fieldGroup: { 
									height: "32px", 
									borderColor: "#5D3FD6",
									borderRadius: "6px"
								},
								field: { fontSize: "13px" }
							}}
						/>
					)}
					{!hide_all && (
						<div style={{ marginBottom: "16px" }}>
							<TextField
								name="username"
								placeholder="Username"
								value={this.state.username}
								onChange={this.handleUsername}
								styles={{ 
									fieldGroup: { 
										height: "32px", 
										borderColor: "#5D3FD6",
										borderRadius: "6px"
									},
									field: { fontSize: "13px" }
								}}
							/>
						</div>
					)}
					{!hide_all && (
						<div style={{ marginBottom: "16px" }}>
							<TextField
								name="password"
								placeholder="Password"
								value={this.state.password}
								onKeyDown={this.keyPress}
								onChange={this.handlePassword}
								type="password"
								canRevealPassword={true}
								styles={{ 
									fieldGroup: { 
										height: "32px", 
										borderColor: "#5D3FD6",
										borderRadius: "6px"
									},
									field: { fontSize: "13px" }
								}}
							/>
						</div>
					)}
					<div style={OTPStyle}>
						<TextField
							name="otp"
							placeholder="One Time Pin"
							value={this.state.otp}
							onKeyDown={this.keyPress}
							onChange={this.handleOpt}
							styles={{ 
								root: { marginBottom: "16px" },
								fieldGroup: { 
									height: "32px", 
									borderColor: "#5D3FD6",
									borderRadius: "6px"
								},
								field: { fontSize: "13px" }
							}}
						/>
					</div>
					{hide_all ? (
						""
					) : (
					<div style={{ marginTop: "24px", marginBottom: "16px", display: "flex", justifyContent: "center" }}>
						<PrimaryButton 
							text="Login" 
							onClick={this.handleLoginClick}
							styles={{ 
								root: { 
									width: "auto", 
									height: "40px", 
									fontSize: "14px",
									fontWeight: 600,
									borderRadius: "8px",
									backgroundColor: "#5D3FD6",
									border: "none",
									padding: "0 20px"
								},
									rootHovered: {
										backgroundColor: "#4A2FC7"
									},
									rootPressed: {
										backgroundColor: "#3B24A8"
									},
									rootDisabled: {
										backgroundColor: "#e0e0e0",
										color: "#666666"
									}
								}}
							/>
						</div>
					)}
					<div style={{ marginBottom: "16px", display: "flex", justifyContent: "center" }}>
						<DefaultButton 
							text="SSO Login" 
							onClick={this.handleSSOLoginClick}
							styles={{ 
								root: { 
									width: "auto", 
									height: "40px", 
									fontSize: "14px",
									fontWeight: 600,
									borderRadius: "8px",
									backgroundColor: "#ffffff",
									color: "#5D3FD6",
									border: "2px solid #5D3FD6",
									padding: "0 20px"
								},
								rootHovered: {
									backgroundColor: "#f8f6fc",
									color: "#4A2FC7",
									border: "2px solid #4A2FC7"
								},
								rootPressed: {
									backgroundColor: "#ede8f7",
									color: "#3B24A8",
									border: "2px solid #3B24A8"
								},
								rootDisabled: {
									backgroundColor: "#f5f5f5",
									color: "#999999",
									border: "2px solid #cccccc"
								}
							}}
						/>

					</div>
					<div style={{ textAlign: "center", marginTop: "8px" }}>
						<span style={{ color: "#b0b0b0", fontSize: "12px", fontWeight: 400 }}>Version: {APP_VERSION}</span>
					</div>
				</div>
				<div style={style}>
					<div style={{ 
						marginTop: "16px", 
						padding: "12px 20px",
						backgroundColor: "#fff",
						borderRadius: "8px",
						maxWidth: "420px",
						margin: "16px auto 0",
						border: "2px solid #ff4444"
					}}>
						<Label style={{ color: "#ff4444", fontSize: "14px", fontWeight: 500, margin: 0 }}>{login_error}</Label>
					</div>
				</div>
			</div>
		);
	}
}

export default Login;
